import "./CancelSubscription.scss";

import { Alert, Button, Dropdown, Flex, Text, TextArea } from "@fluentui/react-northstar";
import FormState, { FormData } from "@shopify/react-form-state";
import { StripeBillingInfoDTO, UserSubscriptionDTO } from "@teams/types/api";
import moment from "moment";
import React, { useMemo, useState } from "react";

import { useUserInfo } from "../../actions/useUserInfo";
import { PollyDialog } from "../../components/PollyDialog";
import { PollyIcon } from "../../components/PollyIcon";
import { isError } from "../../services/ChannelApi";
import { paymentsApi } from "../../services/PaymentsApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { getPlanLabel } from "../../types/subscriptionHelper";
import { isMobileClient } from "../../utils/clientHelper";
import { clientEventTracker } from "../../utils/clientTelemetry";
import { getPageContext } from "../../utils/teamsHelpers";

type CancelSubscriptionProps = {
  subscription: UserSubscriptionDTO;
  stripeBillingInfo: StripeBillingInfoDTO;
  onClose: (reload: boolean) => void;
};

type CancelSubscriptionDetails = {
  reason: string;
  feedback: string;
};

export const CancelSubscription: React.FC<CancelSubscriptionProps> = ({
  subscription,
  stripeBillingInfo,
  onClose,
}) => {
  const { user } = useUserInfo();
  const initialValues = { reason: "", feedback: "" };
  const { teamsContext } = useTeamsContext();
  const [cancellationError, setCancellationError] = useState<Error>();
  const [loadingOffer] = useState<boolean>(false);

  async function save(formFields: FormData<CancelSubscriptionDetails>) {
    const result = await paymentsApi.cancelSubscription(
      teamsContext,
      subscription.id,
      formFields.fields.reason.value,
      formFields.fields.feedback.value
    );
    if (isError(result)) {
      clientEventTracker.track("cancelSubscription/error", user, {
        error: "cancellation error",
        cancellationError,
      });
      setCancellationError(result);
    } else {
      onClose(true);
    }
  }

  const title = useMemo<string>(() => {
    return `Cancel ${stripeBillingInfo.isTrial ? "Trial" : "Subscription"}`;
  }, [stripeBillingInfo]);

  const planLabel = useMemo<string>(() => {
    return `${getPlanLabel(subscription.plan, subscription.planLevel, subscription.scope)} ${
      stripeBillingInfo.isTrial ? "Trial" : ""
    }`;
  }, [subscription, stripeBillingInfo]);

  if (cancellationError) {
    return (
      <PollyDialog
        closeOnOutsideClick={false}
        header={title}
        maxWidth="1200px"
        open
        onCancel={() => onClose(false)}
      >
        <Alert content="Oops, something went wrong please try again or contact support" warning />
      </PollyDialog>
    );
  }

  return (
    <PollyDialog
      closeOnOutsideClick={false}
      header={title}
      maxWidth="500px"
      open
      onCancel={() => onClose(false)}
    >
      <div className="cancel-dialog">
        <Flex gap="gap.medium" column>
          <Text
            content="We're sorry to see you go!"
            align="center"
            as="div"
            className="cancel-subscription__cancel-header"
          />
          <Text content="Please help us understand the reason" align="center" as="div" />
        </Flex>

        <FormState initialValues={initialValues} onSubmit={save}>
          {({ fields, submitting, submit }) => {
            return (
              <Flex column={true} gap="gap.medium" className="cancel-subscription__form">
                <Flex.Item>
                  <Flex gap="gap.medium">
                    <Flex.Item size="size.half">
                      <Flex
                        column={true}
                        gap="gap.medium"
                        className={`full-width ${fields.reason.value ? "" : "cancelation-reason"}`}
                      >
                        <div>
                          <Text weight="regular" content="Reason for cancelling" />
                          <Dropdown
                            placeholder="Choose reason for cancellation"
                            items={[
                              "Don't need to create polls/surveys anymore",
                              "Just signed up for a one time use",
                              "It didn't have the required features",
                              "It's expensive",
                              "I'm leaving the compnay or we are not using MS Teams anymore",
                              "Other (free form below)",
                            ]}
                            onChange={(_e, props) => {
                              if (props?.value) {
                                fields.reason.onChange(props.value as string);
                              }
                            }}
                            fluid
                          />
                        </div>
                        {[
                          "It didn't have the required features",
                          "Other (free form below)",
                        ].includes(fields.reason.value) && (
                          <div>
                            <Text weight="regular" content="How can we improve?" />
                            <TextArea
                              className="cancel-subscription__feedback"
                              variables={{ height: "4.5em" }}
                              onChange={(_e, props) => {
                                if (props?.value) {
                                  fields.feedback.onChange(props.value as string);
                                }
                              }}
                              placeholder={"Let us know if anything didn't work for you..."}
                              fluid
                            />
                          </div>
                        )}
                      </Flex>
                    </Flex.Item>
                  </Flex>
                </Flex.Item>
                {getPageContext() === "/msteams" && (
                  <>
                    <Flex.Item>
                      <Text as="div">
                        Your Polly {planLabel} subscription is{" "}
                        {stripeBillingInfo.isTrial ? "active" : "paid"} until{" "}
                        {moment(stripeBillingInfo.currentPeriodEnd).format("LL")}.
                      </Text>
                    </Flex.Item>
                    <Flex.Item>
                      <Flex className="cancellation-reasons">
                        <Flex.Item align="center">
                          <Flex column={true} gap="gap.small">
                            <Text as="div" align="start">
                              Here’s what you are going to miss
                            </Text>
                            {[
                              "Results older than 45 days",
                              "Creating pollys if you're over your response limit",
                            ].map(row => (
                              <Flex key={row} gap="gap.small" vAlign="center">
                                <PollyIcon icon="yellow-right-arrow" ignoreTheme />
                                <Text content={row} />
                              </Flex>
                            ))}
                          </Flex>
                        </Flex.Item>
                      </Flex>
                    </Flex.Item>
                  </>
                )}
                <Flex.Item align="end">
                  <Flex gap="gap.medium" column={isMobileClient(teamsContext)} className="options">
                    <Button
                      secondary
                      content={`Go Back`}
                      loading={submitting || loadingOffer}
                      onClick={() => onClose(false)}
                    />

                    <Button
                      className={
                        submitting || loadingOffer || !fields.reason.value
                          ? undefined
                          : "cancel-subscription__cancel-btn"
                      }
                      content={`Cancel ${stripeBillingInfo.isTrial ? "Trial" : "Subscription"}`}
                      disabled={submitting || loadingOffer || !fields.reason.value}
                      loading={submitting || loadingOffer}
                      onClick={() => submit()}
                    />
                  </Flex>
                </Flex.Item>
              </Flex>
            );
          }}
        </FormState>
      </div>
    </PollyDialog>
  );
};

export const CancelSubscriptionConfirmation: React.FC<{
  onClose: () => void;
  onConfirm: () => void;
  isTrial?: boolean;
}> = ({ onClose, onConfirm, isTrial }) => {
  const { teamsContext } = useTeamsContext();
  return (
    <PollyDialog
      closeOnOutsideClick
      header={`Cancel ${isTrial ? "Trial" : "Subscription"}`}
      maxWidth="450px"
      open
      onCancel={() => onClose()}
    >
      <Flex className="cancel-confirmation" column gap="gap.large">
        <Flex className="prompt">
          <Text
            content="Are you sure you want to cancel your subscription?"
            size="large"
            className="font"
          />
        </Flex>
        <Flex column={isMobileClient(teamsContext)} gap="gap.medium" className="options">
          <Button content="Yes, cancel" onClick={onConfirm} className="confirm-button" />
          <Button content="No, go back" onClick={onClose} secondary />
        </Flex>
      </Flex>
    </PollyDialog>
  );
};
